import React, { useEffect, useState } from "react";
import { db } from "../firebase";
import { collection, addDoc, serverTimestamp, doc, updateDoc } from "firebase/firestore";
import { toAbsoluteUrl } from "../utils/url";
function AdminPortfolioForm({ docId = null, initial = null, onSaved = () => {}, onCancel = null }) {
  const [form, setForm] = useState({
    title: "",
    liveLink: "",
    githubLink: "",
    shortDesc: "",
    imageUrl: "",
    tags: "",
    tech: "",
    detailedDesc: "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  useEffect(() => {
    if (initial) {
      setForm({
        title: initial.title || "",
        liveLink: initial.liveLink || "",
        githubLink: initial.githubLink || "",
        shortDesc: initial.shortDesc || "",
        imageUrl: initial.imageUrl || "",
        tags: (initial.tags || []).join(", "),
        tech: (initial.tech || []).join(", "),
        detailedDesc: initial.detailedDesc || "",
      });
    }
  }, [initial]);

  const onChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  };

  const validate = () => {
    const errs = [];
    if (!form.title.trim()) errs.push("Title is required.");
    if (!form.liveLink.trim()) errs.push("Live link is required.");
    if (!form.shortDesc.trim()) errs.push("Short description is required.");
    if (!form.imageUrl.trim()) errs.push("Image URL is required.");
    return errs;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");
    const errs = validate();
    if (errs.length) {
      setError(errs.join(" "));
      return;
    }
    setLoading(true);
    try {
      const payload = {
        title: form.title.trim(),
        liveLink: toAbsoluteUrl(form.liveLink.trim()),
        githubLink: form.githubLink.trim() || null,
        shortDesc: form.shortDesc.trim(),
        imageUrl: form.imageUrl.trim(),
        // split comma-separated lists and trim
        tags: form.tags
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean),
        tech: form.tech
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean),
        detailedDesc: form.detailedDesc.trim() || null,
      };
      if (docId) {
        await updateDoc(doc(db, "projects", docId), payload);
        setSuccess("Project updated successfully.");
      } else {
        await addDoc(collection(db, "projects"), { ...payload, createdAt: serverTimestamp() });
        setSuccess("Project saved successfully.");
        setForm({
          title: "",
          liveLink: "",
          githubLink: "",
          shortDesc: "",
          imageUrl: "",
          tags: "",
          tech: "",
          detailedDesc: "",
        });
      }
      onSaved();
    } catch (err) {
      setError(err.message || "Could not save project.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-4">
      {error && (
        <div className="text-sm font-semibold text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2">
          {error}
        </div>
      )}
      {success && (
        <div className="text-sm font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-md px-3 py-2">
          {success}
        </div>
      )}
      <form onSubmit={onSubmit} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="sm:col-span-2">
          <label className="block text-sm font-semibold text-gray-700 mb-1">Title *</label>
          <input name="title" value={form.title} onChange={onChange} className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400" placeholder="Project title" />
        </div>
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-1">Live Link *</label>
          <input name="liveLink" value={form.liveLink} onChange={onChange} className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400" placeholder="https://example.com" />
        </div>
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-1">GitHub Link (optional)</label>
          <input name="githubLink" value={form.githubLink} onChange={onChange} className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400" placeholder="https://github.com/username/repo" />
        </div>
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-1">Image URL *</label>
          <input name="imageUrl" value={form.imageUrl} onChange={onChange} className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400" placeholder="https://.../image.png" />
        </div>
        <div className="sm:col-span-2">
          <label className="block text-sm font-semibold text-gray-700 mb-1">Short Description (1-2 lines) *</label>
          <input name="shortDesc" value={form.shortDesc} onChange={onChange} className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400" placeholder="Brief description" />
        </div>
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-1">Tags (comma-separated)</label>
          <input name="tags" value={form.tags} onChange={onChange} className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400" placeholder="UI/UX, App Design" />
        </div>
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-1">Tech Stack (comma-separated)</label>
          <input name="tech" value={form.tech} onChange={onChange} className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400" placeholder="React, Tailwind, Firebase" />
        </div>
        <div className="sm:col-span-2">
          <label className="block text-sm font-semibold text-gray-700 mb-1">Detailed Description (optional)</label>
          <textarea name="detailedDesc" value={form.detailedDesc} onChange={onChange} rows={5} className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400" placeholder="Add more details if needed" />
        </div>
        <div className="sm:col-span-2 flex items-center gap-3">
          <button type="submit" disabled={loading} className="flex items-center gap-2 font-semibold bg-[#211033] text-white border border-teal-700 rounded-full py-2 px-5 hover:bg-white hover:text-[#211033] ease-in duration-300 shadow disabled:opacity-60 disabled:cursor-not-allowed">
            {loading ? (docId ? "Updating..." : "Saving...") : docId ? "Update Project" : "Save Project"}
          </button>
          {onCancel && (
            <button type="button" onClick={onCancel} className="text-sm font-semibold text-teal-700 hover:underline">Cancel</button>
          )}
        </div>
      </form>
    </div>
  );
}

export default AdminPortfolioForm;
